import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { getApiBase, getAuthHeaders } from "@/lib/api";
import { useLocation } from "wouter";

interface AudioControlContextValue {
  voiceEnabled: boolean;
  setVoiceEnabled: (enabled: boolean) => void;
  isSpeaking: boolean;
  isRecording: boolean;
  recordingDuration: number;
  recordingTranscript: string;
  speakText: (text: string, force?: boolean) => Promise<void>;
  stopSpeaking: () => void;
  startRecording: (onError?: (error: { title: string; description: string }) => void) => void;
  stopRecording: () => Promise<string>;
  cancelRecording: () => void;
  isTranscribing: boolean;
  liveAudioActive: boolean;
  claimLiveAudio: () => void;
  releaseLiveAudio: () => void;
  currentPage: string;
}

const AudioControlContext = createContext<AudioControlContextValue | null>(null);

export function AudioControlProvider({ children }: { children: ReactNode }) {
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return localStorage.getItem("plate-os-voice-enabled") === "true"; } catch { return false; }
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingTranscript, setRecordingTranscript] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [liveAudioActive, setLiveAudioActive] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioBlobUrlRef = useRef<string | null>(null);
  const ttsAbortRef = useRef<AbortController | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const [location] = useLocation();

  useEffect(() => {
    try { localStorage.setItem("plate-os-voice-enabled", String(voiceEnabled)); } catch {}
  }, [voiceEnabled]);

  useEffect(() => {
    return () => {
      ttsAbortRef.current?.abort();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioBlobUrlRef.current) {
        URL.revokeObjectURL(audioBlobUrlRef.current);
        audioBlobUrlRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, []);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }
    if (audioBlobUrlRef.current) {
      URL.revokeObjectURL(audioBlobUrlRef.current);
      audioBlobUrlRef.current = null;
    }
  }, []);

  const speakText = useCallback(async (text: string, force?: boolean) => {
    if (liveAudioActive || ((!voiceEnabled && !force) || !text.trim())) return;

    ttsAbortRef.current?.abort();
    cleanupAudio();

    const controller = new AbortController();
    ttsAbortRef.current = controller;

    try {
      setIsSpeaking(true);
      const base = getApiBase();
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${base}/plate-talk/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        credentials: "include",
        body: JSON.stringify({ text: text.substring(0, 4000) }),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.error("TTS failed:", response.status);
        setIsSpeaking(false);
        return;
      }
      if (controller.signal.aborted) {
        setIsSpeaking(false);
        return;
      }
      const blob = await response.blob();
      if (controller.signal.aborted) {
        setIsSpeaking(false);
        return;
      }
      const url = URL.createObjectURL(blob);
      audioBlobUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setIsSpeaking(false);
        cleanupAudio();
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        cleanupAudio();
      };
      await audio.play();
    } catch (err: any) {
      if (err.name === "AbortError") {
        setIsSpeaking(false);
        return;
      }
      console.error("TTS error:", err);
      setIsSpeaking(false);
    }
  }, [liveAudioActive, voiceEnabled, cleanupAudio]);

  const claimLiveAudio = useCallback(() => {
    ttsAbortRef.current?.abort();
    cleanupAudio();
    setIsSpeaking(false);
    setLiveAudioActive(true);
  }, [cleanupAudio]);

  const releaseLiveAudio = useCallback(() => {
    setLiveAudioActive(false);
  }, []);

  const stopSpeaking = useCallback(() => {
    ttsAbortRef.current?.abort();
    cleanupAudio();
    setIsSpeaking(false);
  }, [cleanupAudio]);

  const cleanupRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setRecordingDuration(0);
    setRecordingTranscript("");
    setIsRecording(false);
  }, []);

  const startRecording = useCallback((onError?: (error: { title: string; description: string }) => void) => {
    if (isRecording) return;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
            ? "audio/webm"
            : "audio/mp4";

        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;
        audioChunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            audioChunksRef.current.push(e.data);
          }
        };

        recorder.start(1000);

        recordingStartRef.current = Date.now();
        setIsRecording(true);
        setRecordingDuration(0);
        setRecordingTranscript("");

        recordingTimerRef.current = setInterval(() => {
          setRecordingDuration(Math.floor((Date.now() - recordingStartRef.current) / 1000));
        }, 500);
      } catch (err: any) {
        cleanupRecording();
        if (err.name === "NotAllowedError") {
          onError?.({ title: "Microphone blocked", description: "Please allow microphone access in your browser settings." });
        } else {
          onError?.({ title: "Recording failed", description: "Could not start audio recording. Please try again." });
        }
      }
    })();
  }, [isRecording, cleanupRecording]);

  const stopRecording = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        cleanupRecording();
        resolve("");
        return;
      }

      setIsTranscribing(true);
      setRecordingTranscript("Transcribing...");

      recorder.onstop = async () => {
        const chunks = audioChunksRef.current;
        const mimeType = recorder.mimeType || "audio/webm";

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

        if (chunks.length === 0) {
          setIsRecording(false);
          setIsTranscribing(false);
          setRecordingTranscript("");
          setRecordingDuration(0);
          audioChunksRef.current = [];
          mediaRecorderRef.current = null;
          resolve("");
          return;
        }

        const blob = new Blob(chunks, { type: mimeType });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;

        try {
          const reader = new FileReader();
          const base64 = await new Promise<string>((res, rej) => {
            reader.onload = () => {
              const dataUrl = reader.result as string;
              const b64 = dataUrl.split(",")[1] || "";
              res(b64);
            };
            reader.onerror = rej;
            reader.readAsDataURL(blob);
          });

          const apiBase = getApiBase();
          const authHeaders = await getAuthHeaders();
          const resp = await fetch(`${apiBase}/plate-talk/transcribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            credentials: "include",
            body: JSON.stringify({ audio: base64, mimeType: mimeType.split(";")[0] }),
          });

          if (!resp.ok) {
            console.error("[recording] Transcription API error:", resp.status);
            setIsRecording(false);
            setIsTranscribing(false);
            setRecordingTranscript("");
            setRecordingDuration(0);
            resolve("");
            return;
          }

          const data = await resp.json();
          const transcript = (data.transcript || "").trim();
          setIsRecording(false);
          setIsTranscribing(false);
          setRecordingTranscript("");
          setRecordingDuration(0);
          resolve(transcript);
        } catch (err) {
          console.error("[recording] Transcription error:", err);
          setIsRecording(false);
          setIsTranscribing(false);
          setRecordingTranscript("");
          setRecordingDuration(0);
          resolve("");
        }
      };

      recorder.stop();
    });
  }, [cleanupRecording]);

  const cancelRecording = useCallback(() => {
    cleanupRecording();
    setIsTranscribing(false);
  }, [cleanupRecording]);

  return (
    <AudioControlContext.Provider value={{
      voiceEnabled,
      setVoiceEnabled,
      isSpeaking,
      isRecording,
      recordingDuration,
      recordingTranscript,
      speakText,
      stopSpeaking,
      startRecording,
      stopRecording,
      cancelRecording,
      isTranscribing,
      liveAudioActive,
      claimLiveAudio,
      releaseLiveAudio,
      currentPage: location,
    }}>
      {children}
    </AudioControlContext.Provider>
  );
}

export function useAudioControl() {
  const ctx = useContext(AudioControlContext);
  if (!ctx) throw new Error("useAudioControl must be used within AudioControlProvider");
  return ctx;
}
